/**
 * Socket communication helper for Office and AIBox
 */

import { concatByteArray } from "../crypto/utility";
import { FileDownloaderService } from "../services/file-downloader.service";

export type CommunicatorComponent = {
    openDocumentEditor: (uri, num)=>void,
    streamMessageFromFrameToNano: (srv_buf)=>void,
    postMessageToFrame: (msg: Object)=>void,
    closeOpenEditor: ()=>void,
    frameSock: FrameSock | undefined | null,
    fileDownloaderService: FileDownloaderService
}

// the rest is to be closely reused
export class FrameSock {
  public num; // session for frame communication
  public uri;
  public max_delay;
  public part_size;
  public srv_n = 0;
  public srv_buf = [];
  public srv_buf_size = 0;
  // public _dbg_srv_buf_start = 0;
  public srv_drain_hndl = null;
  public client_n = 0;
  public client_buf = [];
  public buf_to_client = {}; // FIXME: this is temporary for a bug workaround
  public client_buf_size = 0;
  public client_buf_target = 0;
  public _http_requests = {};
  public componentRef: CommunicatorComponent;

  static frameSockHttpReqNum = 0;

  // For UX the typing-related messages are to be sent immediately, without batch-buffering delay.
  static NO_DELAY_MESSAGES = [
    new Uint8Array([116, 101, 120, 116, 105, 110, 112, 117, 116, 32]), // b"textinput "
    new Uint8Array([
      114, 101, 109, 111, 118, 101, 116, 101, 120, 116, 99, 111, 110, 116, 101, 120, 116, 32,
    ]), // b"removecontext "
  ];

  constructor(num, uri, max_delay, part_size, componentRef: CommunicatorComponent) {
    this.num = num; // session for frame communication
    this.uri = uri;
    this.max_delay = max_delay || 2;
    this.part_size = part_size || 500000;
    this.srv_n = 0;
    this.srv_buf = [];
    this.srv_buf_size = 0;
    // this._dbg_srv_buf_start = 0;
    this.srv_drain_hndl = null;
    this.client_n = 0;
    this.client_buf = [];
    this.buf_to_client = {};
    this.client_buf_size = 0;
    this.client_buf_target = 0;
    this._http_requests = {};
    this.componentRef = componentRef;

    // init
    this.componentRef.openDocumentEditor(this.uri, this.num); // DOC-OPN request
  }

  public push_to_server = (msg) => {
    if (msg instanceof Blob) {
      msg
        .arrayBuffer()
        .then((res) => this.push_to_server(new Uint8Array(res)))
        .catch((err) => console.log(err));
      return;
    }
    if (typeof msg === 'string') {
      msg = new TextEncoder().encode(msg);
    }
    this._push_to_server_inner(msg);
  };

  private _push_to_server_inner = (msg) => {
    // if (this.srv_buf.length === 0) {
    //   let xx = performance.now();
    //   console.log('_MSG_BUF_MERGE_TIME_NEEDED', xx - this._dbg_srv_buf_start);
    //   this._dbg_srv_buf_start = xx;
    // }
    var offset = 0;
    var size = msg.length;
    while (1) {
      let chunk_end = Math.min(
        offset + Math.min(size - offset, this.part_size - this.srv_buf_size),
        size
      );
      let chunk = msg.slice(offset, chunk_end);
      this.srv_buf.push([this.srv_n, offset, size, chunk]);
      this.srv_buf_size += 16 + chunk.length;
      if (chunk_end === size) {
        break;
      } else if (this.srv_buf_size >= this.part_size) {
        this._drain_srv_buf();
      }
      offset = chunk_end;
    }
    // if the message is latency-critical (for UX), drain immediately
    for (let lcm of FrameSock.NO_DELAY_MESSAGES) {
      if (msg.length >= lcm.length) {
        let i = 0;
        while (i < lcm.length && msg[i] === lcm[i]) ++i;
        if (i === lcm.length) {
          this._drain_srv_buf();
          break;
        }
      }
    }
    this.srv_n += 1;
    if (this.srv_buf.length > 0 && !this.srv_drain_hndl) {
      this.srv_drain_hndl = setTimeout(() => {
        this._drain_srv_buf();
      }, this.max_delay);
    }
  };

  private _drain_srv_buf = () => {
    if (this.srv_buf.length > 0) {
      // console.log('_MSG', this.srv_buf.length, performance.now() - this._dbg_srv_buf_start);
      this.componentRef.streamMessageFromFrameToNano(this.srv_buf); // DOC-TX request
      this.srv_buf = [];
      this.srv_buf_size = 0;
      // this._dbg_srv_buf_start = performance.now();
    }
    if (this.srv_drain_hndl) {
      clearTimeout(this.srv_drain_hndl);
      this.srv_drain_hndl = null;
    }
  };

  public push_to_client = (msgs) => {
    let i = 0;
    while (i < msgs.length) {
      let packed_msg = msgs[i];
      // FIXME: this part with the temporary gap-buffer is a workaround for the local subscription processing not
      //  keeping the payload callbacks in order. I'm guessing that if there is a smaller part, that gets decrypted
      //  sooner, that will be propagated sooner. (often reproducible on Firefox)
      if (packed_msg[0] === this.client_n) {
        this._push_to_client_inner(packed_msg);
      } else {
        this.buf_to_client[packed_msg[0]] = packed_msg;
        console.warn('Buffering ws message to frame (workaround)', packed_msg[0]);
      }
      i += 1;
    }
    // FIXME: process the gap buffer; note that no gap-size limit detection is present
    while (1) {
      let packed_msg = this.buf_to_client[this.client_n];
      if (packed_msg && packed_msg[0] === this.client_n) {
        delete this.buf_to_client[this.client_n];
        console.warn('Consuming buffered ws message to frame (workaround)', packed_msg[0]);
        this._push_to_client_inner(packed_msg);
      } else {
        break;
      }
    }
  };

  private _push_to_client_inner = (packed_msg) => {
    if (packed_msg[0] !== this.client_n) {
      throw 'Ws message num inconsistent ' + packed_msg[0] + ' ' + this.client_n;
    }
    if (packed_msg[1] !== this.client_buf_size) {
      throw 'Ws message offset inconsistent';
    }
    if (packed_msg[1] === 0) {
      this.client_buf_target = packed_msg[2];
    } else if (packed_msg[2] !== this.client_buf_target) {
      throw 'Ws message len inconsistent';
    }
    var chunk = packed_msg[3];
    this.client_buf.push(chunk);
    this.client_buf_size += chunk.length;
    if (this.client_buf_size === this.client_buf_target) {
      this._handle_client_msg(concatByteArray(...this.client_buf));
      this.client_buf_size = 0;
      this.client_buf = [];
      this.client_n += 1;
    }
  };

  private _handle_client_msg = (data) => {
    // NOTE: we could assume that data is Uint8Array, because the proxy transport is binary-only
    if (data instanceof Uint8Array && data.length >= 16) {
      if (
        data[0] === 110 &&
        data[1] === 97 &&
        data[2] === 110 &&
        data[3] === 111 &&
        data[4] === 104 &&
        data[5] === 116 &&
        data[6] === 116 &&
        data[7] === 112 &&
        data[8] === 112 &&
        data[9] === 114 &&
        data[10] === 111 &&
        data[11] === 120 &&
        data[12] === 121 &&
        data[13] === 32
      ) {
        // data starts with "nanohttpproxy "
        var op_newline_pos = data.indexOf(10, 14);
        if (op_newline_pos === -1) {
          console.error('Malformed app nanohttpproxy response', data);
          return;
        }
        var req_num = new TextDecoder().decode(data.slice(14, op_newline_pos));
        var req = this._http_requests[req_num];
        if (req) {
          delete this._http_requests[req_num];
          if (req[2]) {
            clearTimeout(req[2]);
          }
          var head_newline_pos = data.indexOf(10, op_newline_pos + 1);
          if (head_newline_pos === -1) {
            console.warn('Malformed app nanohttpproxy response head', data);
            req[1]();
            return;
          }
          try {
            var resp_head = JSON.parse(
              new TextDecoder().decode(data.slice(op_newline_pos + 1, head_newline_pos))
            );
          } catch {
            console.warn('Invalid app nanohttpproxy response head', data);
            req[1]();
            return;
          }
          req[0](resp_head, data.slice(head_newline_pos + 1));
          return;
        }
        // fallthrough, the frame will handle it
      }
    }
    this.componentRef.postMessageToFrame({
      ws: 'message',
      num: this.num,
      data: data,
    });
  };

  public http_proxy_get = (url, success_cb, error_cb, timeout) => {
    return this._http_proxy_req('GET', url, {}, success_cb, error_cb, timeout);
  };
  public http_proxy_post = (url, fields, success_cb, error_cb, timeout) => {
    return this._http_proxy_req('POST', url, fields, success_cb, error_cb, timeout);
  };

  private _http_proxy_req = (method, url, fields, success_cb, error_cb, timeout) => {
    var req_num = 'fs' + ++FrameSock.frameSockHttpReqNum;
    var msg_core = { method: method, url: url };
    var bin_fields = [];
    if (method === 'POST' && fields) {
      msg_core['fields'] = {};
      for (var pair of fields) {
        if (pair[1] instanceof Uint8Array) {
          bin_fields.push([pair[0], pair[1]]);
        } else if (pair[1] instanceof ArrayBuffer) {
          bin_fields.push([pair[0], new Uint8Array(pair[1])]);
        } else {
          msg_core['fields'][pair[0]] = '' + pair[1];
        }
      }
    }
    var msg_parts = [
      new TextEncoder().encode('nanohttpproxy ' + req_num + '\n'),
      new TextEncoder().encode(JSON.stringify(msg_core)),
    ];
    for (var i = 0; i < bin_fields.length; ++i) {
      var bin_field_pair = bin_fields[i];
      var msg_field = { name: bin_field_pair[0] };
      msg_field['len'] = bin_field_pair[1].length;
      msg_parts.push(new TextEncoder().encode('\n' + JSON.stringify(msg_field) + '\n'));
      msg_parts.push(bin_field_pair[1]);
    }
    // assemble full msg
    this.push_to_server(concatByteArray(...msg_parts));
    var timeout_h = null;
    if (timeout && timeout > 0) {
      timeout_h = setTimeout(
        (function (that, k) {
          return function () {
            that._http_proxy_req_timeout(k);
          };
        })(this, req_num),
        timeout
      );
    }
    this._http_requests[req_num] = [success_cb, error_cb, timeout_h];
  };

  private _http_proxy_req_timeout = (req_num) => {
    var req = this._http_requests[req_num];
    if (req) {
      delete this._http_requests[req_num];
      req[1]();
    }
  };

  public close = (error: boolean = false) => {
    if (this.srv_drain_hndl) {
      clearTimeout(this.srv_drain_hndl);
      this.srv_drain_hndl = null;
    }
    this.componentRef.postMessageToFrame({ ws: error ? 'error' : 'close', num: this.num });
    this.componentRef.closeOpenEditor(); // DOC-CLS request
  };
}

export enum FrameMessageId {
  APP_LOADING_STATUS = 'App_LoadingStatus',
  HOST_POST_MESSAGE_READY = 'Host_PostmessageReady',
  DOWNLOAD_AS = 'Download_As',
}

export function onFrameMessage(e: MessageEvent, component: CommunicatorComponent): void {
    if (typeof e.data === 'string') {
      // WOPI PostMessage handling.
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch (err) {
        return;
      }

      // console.log('WOPI SendMessage', msg);
      if (msg.MessageId === FrameMessageId.APP_LOADING_STATUS) {
        component.postMessageToFrame({
          MessageId: FrameMessageId.HOST_POST_MESSAGE_READY,
          SendTime: Date.now(),
          Values: {},
        });
      } else if (msg.MessageId === FrameMessageId.DOWNLOAD_AS) {
        // window.open(msg.Values.URL, "_blank");
        component.frameSock.http_proxy_get(
          msg.Values.URL,
          (info, body) => _download_as(component.fileDownloaderService, body, info.content_type, info.filename),
          function (e) {
            console.error('Download_As proxy err', e);
          },
          30 * 1000
        );
      }
      return;
    } else if (!(e.data.ws && e.data.num)) {
      return;
    }

    if (e.data.ws === 'abandon') {
      //console.log('abandon msg', e);
      if (component.frameSock && e.data.num === component.frameSock.num) {
        terminateFrameSock(component, 'Abandon by frame');
      }
      return;
    }
    if (!component.frameSock) {
      if (e.data.ws === 'open') {
        if (component.frameSock) {
            terminateFrameSock(component, 'Replacing');
        }
        // createFrameSock
        component.frameSock = new FrameSock(e.data.num, e.data.uri, undefined, undefined, component);
        console.log('Created FrameSock ' + component.frameSock.num);
      } else {
        console.warn('Frame message while no sock', e);
        component.postMessageToFrame({ ws: 'error', num: e.data.num });
      }
    } else {
      if (e.data.num !== component.frameSock.num) {
        console.warn('Frame message with inconsistent num', e);
        component.postMessageToFrame({ ws: 'error', num: e.data.num });
      } else if (e.data.ws === 'send') {
        component.frameSock.push_to_server(e.data.msg);
      } else if (e.data.ws === 'close') {
        terminateFrameSock(component);
      } else {
        console.warn('Frame message unknown', e);
        component.postMessageToFrame({ ws: 'error', num: e.data.num });
      }
    }
  }

  export function terminateFrameSock(component: CommunicatorComponent, error: string = '') {
    if (!component.frameSock) {
      console.log('No FrameSock to terminate', error);
      return;
    }
    console.log('Closing FrameSock ' + component.frameSock.num + ' with', error);
    component.frameSock.close(!!error);
    component.frameSock = null;
  }

  // NOTE: this utility should be carefully merged into BlobService?
  function _download_as(fileDownloaderService: FileDownloaderService, data, mimeType, fileName) {
    mimeType = 'application/octet-stream'; // force this for safety
    if (!fileName || (typeof fileName !== 'string' && !(fileName instanceof String))) {
      fileName = 'file';
    }
    var blob = new Blob([data], { type: mimeType });

    fileDownloaderService.download(blob, fileName);
  }